import { Inventory } from "@shared/types";
import { GameRouteService } from "../services/GameRouteService";
import { css, htmlArray } from "../helpers/webComponents";

/** CSS affecting the {@link InventoryComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        text-transform: uppercase;
    }

    .inventory-icon {
        position: absolute;
        animation: moveAround 10s infinite;
        z-index: 1;
        img {
            width: 120px;
            height: auto;
            cursor: pointer;
        }
    }

    .inventory-header {
    font-size: 22px;
    padding: 15px;
    border-bottom: 5px solid white;
    background: #2c3e50;
    color: white;
    text-transform: uppercase;
    font-weight: bold;
    text-align: center;
}

.inventory-title {
    font-size: 26px;
    padding: 15px 0;
    text-align: center;
    color: #ced5db;
}

.inventory-items {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    padding: 20px;
    justify-content: center;
}

.item {
    flex: 1 1 calc(33.33% - 20px);
    background: #dde1e7;
    border-radius: 12px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    padding: 15px;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    cursor: pointer;
    overflow: hidden;
}

.item:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
}

.item img {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    margin-right: 15px;
    object-fit: cover;
}

.item-text {
    font-size: 18px;
    font-weight: bold;
    color: #2c3e50;
}

    @keyframes moveAround {
        0%   {top: 0px; left: 0px; background: red;}
        25%  {top: 0px; left: calc(100% - 120px); background: blue;}
        50%  {top: calc(100% - 130px); left: calc(100% - 120px);; background: yellow;}
        75%  {top: calc(100% - 130px); left: 0px; background: green;}
        100% {top: 0px; left: 0px; background: red;}
    }
`;

/** Represents the InventoryComponent */
export class InventoryComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private isOpen: boolean = false;
    private defaultInventory: Inventory = { inventory: [] };
    private inventory: Inventory = this.defaultInventory;

    /**
     * Creates a new instance of the InventoryComponent
    */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.addEventListener("click", () => this.toggleInventory());
        document.addEventListener("click", this.handleOutsideClick.bind(this));

        void this.refreshInventory();
        this.render();
    }

    /**
     * Cleanup when the component is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        document.removeEventListener("click", this.handleOutsideClick.bind(this));
    }

    /**
     * Refreshes the inventory
     */
    private async refreshInventory(): Promise<void> {
        try {
            const inventoryResponse: Inventory = await this._gameRouteService.getInventory();

            this.updateInventory(inventoryResponse.inventory);
        }
        catch (error) {
            console.error("Failed to fetch inventory:", error);
        }
    }

    /**
     * Updates the inventory
     * @param inventoryItems The inventory items to update
     */
    private updateInventory(inventoryItems: string[]): void {
        if (Array.isArray(inventoryItems)) {
            this.inventory = { inventory: inventoryItems };
        }
        else {
            console.error("Unexpected inventory structure:", inventoryItems);
            this.inventory = this.defaultInventory;
        }

        this.render();
    }

    private toggleInventory(): void {
        this.isOpen = !this.isOpen;
        void this.refreshInventory();
        this.render();
    }

    /**
     * Handles clicks outside of the inventory Icon
     */
    private handleOutsideClick(event: MouseEvent): void {
        if (this.isOpen && !this.contains(event.target as Node)) {
            this.isOpen = false;
            this.render();
        }
    }

    /**
     * Renders the InventoryComponent to the DOM
     */
    private render(): void {
        if (!this.shadowRoot) return;

        const elements: HTMLElement[] = htmlArray`
            <style>
                .inventory-box {
                    display: ${this.isOpen ? "block" : "none"};
                    position: absolute;
                    z-index: 1;
                    width: 80%;
                    max-height: 80%;
                    height: 80%;
                    overflow-y: auto;
                    top: 100px;
                    bottom: 100px;
                    left: 10%;
                    right: 0;
                    background: rgba(194, 214, 226, 0.8);
                    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
                    border: 1px solid white;
                    color: black;
                }

                ${styles}
            </style>

            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    private renderContent(): string {
        return `
            <div class="content">
                <div class="inventory-icon">
                    <img src="/assets/img/rooms/Icons_PNG/BagPack.png">
                </div>
                <div class="inventory-box">
                    <div class="inventory-header">
                        <strong class="inventory-title">Inventory</strong>
                    </div>
                    <div class="inventory-items">
                        ${this.inventory.inventory.length > 0
                            ? this.inventory.inventory.map(item => `
                                <div class="item">
                                    <img src="/assets/img/rooms/Icons_PNG/${item}.png">
                                    <div>${item}</div>
                                </div>`).join("")
                            : "<div>Inventory is empty</div>"
                        }
                    </div>
                </div>
            </div>
        `;
    }
}
